import { makeObservable } from '../../shared/stores/core/make-observable';
import { delegate, getDevicePixelRatio } from './lib/utility/dom-helpers';

// This class contains stuff to control the render pipeline.

// Represents arrays of fixed zooms that viewports are allowed to be scaled to. There are 3 options:
// NORMAL are for production use
// EXTENDED and ALL are for debugging and manual testing purposes
export const FORCED_MAGS = {
  NORMAL: [1, 2, 4, 10, 20, 40, 60, 83, 100],
  EXTENDED: [1, 1.25, 2, 4, 5, 8, 9, 10, 11, 12, 15, 16, 20, 40, 60, 83, 100],
  ALL: [...new Array(200)].map((a, i) => i + 1)
};

export const renderSettings = makeObservable({
  // Compensate for IQ issues caused by users scaling the UI (usually on HiDPI displays)
  enableHiDpiCompensation: true,
  // When HiDPI displays are compensated, raw pixels are rendered, which limits the users ability to zoom in. Enabling
  // "Digital zoom" allows users to scale beyond the base layer.
  // Note - this setting is unused. The current HiDpi Compensation implementation directly
  //        alters the base level, which is wrong, but has the beneficial side effect of normalizing
  //        the size of the image, so this setting is unnecessary. Preserving for when we fix the base
  //        mag issue noted above.
  enableDigitalZoom: true,
  // Aperio scanners typically scan at 20x/0.5MPP; images scanned with lower MPPs appear larger at the same
  // magnification. We can compensate for this by scaling the images down. Note that this setting reduces image quality,
  // even at pyramid levels, because we're either making the image smaller or larger to ensure the size is the same.
  // This "correction" was introduced after a single customer complained that slides scanned on different scanners
  // appeared different sizes for the same magnification. There is some debate about the desirability of this feature,
  // since after all it does reduce image quality and no formal VOC was ever performed, however for now Leica is
  // unwilling to back the feature out.
  enableAppMagNormalization: true,
  // List of magnifications a user can select from. Really only used by extensions (zoom buttons, raster scan) to ensure
  // consistency.
  magnificationList: FORCED_MAGS.NORMAL,
  // When HiDPI compensation or appMagNormalization are enabled IQ issues may be present at pyramid levels. Including
  // pyramid levels in the selectable zoom list allows SQA to test IQ issues by being able to view and directly
  // select pyramid levels so they can evaluate the hiDpi or normalization compensation algorithms.
  showAllPyramidLevelsInZoomControl: false,
  // if true pyramid levels will be highlighted in the mag bar
  highlightPyramidLevels: false
});

/**
 * Clamps window.devicePixelRatio to the next highest whole number.
 * @returns {number}
 */
export function getHiDpiRatio () {
  if (renderSettings.enableHiDpiCompensation) {
    // The upscale algo only works with whole multiples of the base level, so always round up to the nearest int
    return Math.ceil(getDevicePixelRatio());
  }
  return 1;
}

/**
 * SQA CODE ONLY BELOW HERE.
 * These settings are to simplify the process of evaluating image quality at pyramid levels. We do not want
 * customers to see these settings so we expose them through a superSecretKey.
 */

renderSettings.save = () => {
  localStorage.setItem('wv:renderSettings', JSON.stringify(renderSettings));
};
renderSettings.restore = () => {
  let tmp = localStorage.getItem('wv:renderSettings');
  tmp = JSON.parse(tmp) || {};
  Object.keys(tmp).forEach((key) => {
    renderSettings[key] = tmp[key];
  });
};
renderSettings.init = () => {
  function toggleDebugImageQuality () {
    isIqDebugEnabled = !isIqDebugEnabled;
    renderSettings.showAllPyramidLevelsInZoomControl = isIqDebugEnabled;
    renderSettings.highlightPyramidLevels = isIqDebugEnabled;
    renderSettings.save();
    localStorage.setItem('wv:renderSettings-debug', isIqDebugEnabled);
    return isIqDebugEnabled;
  }

  let isIqDebugEnabled = localStorage.getItem('wv:renderSettings-debug');
  if (isIqDebugEnabled) {
    isIqDebugEnabled = JSON.parse(isIqDebugEnabled);
  }
  if (isIqDebugEnabled) {
    renderSettings.restore();
  }
  const superSecretKey = 'wwssadad';
  let keyBuffer = '';
  delegate(document, 'keypress', '.lbs-viewer-tab-button', (e) => {
    keyBuffer += e.key;
    if (!superSecretKey.startsWith(keyBuffer)) {
      keyBuffer = '';
    } else if (keyBuffer === superSecretKey) {
      toggleDebugImageQuality();
      keyBuffer = '';
    }
  });
};



// WEBPACK FOOTER //
// ./src/uScope/js/render-settings.js